import { useEffect, useState } from 'react';

import NextImage from 'next/image';

import ROUTES from 'config/routes';
import { ChangeLog, fetchChangelog } from 'shared/api/changelog';
import useTranslation from 'shared/hooks/useTranslation';
import { Divider } from 'ui/shared/atoms/Divider';
import { Typography } from 'ui/shared/molecules/Typography/Typography';

import { LinkInExpandedMenu } from '../LinkInExpandedMenu/LinkInExpandedMenu';
import styles from './ChangeLogContent.module.scss';

function truncateHtmlContent(html: string, maxLength: number = 300): string {
  if (html.length <= maxLength) return html;

  let result = '';
  let currentLength = 0;
  let inTag = false;
  let currentTag = '';
  const openTags: string[] = [];

  for (let i = 0; i < html.length; i++) {
    const char = html[i];

    if (char === '<') {
      inTag = true;
      currentTag = '';
      result += char;
    } else if (char === '>') {
      inTag = false;
      result += char;

      // Check if it's a closing tag
      if (currentTag.startsWith('/')) {
        const tagName = currentTag.substring(1).trim();
        if (openTags.length > 0 && openTags[openTags.length - 1] === tagName) {
          openTags.pop();
        }
      }
      // Check if it's an opening tag (not self-closing)
      else if (!currentTag.endsWith('/') && currentTag.trim() !== '') {
        const tagName = currentTag.split(/\s+/)[0].trim();
        if (tagName !== 'br' && tagName !== 'img' && tagName !== 'hr' && tagName !== 'input') {
          openTags.push(tagName);
        }
      }
    } else if (inTag) {
      currentTag += char;
      result += char;
    } else {
      // We're in text content
      if (currentLength < maxLength) {
        result += char;
        currentLength++;
      } else if (currentLength === maxLength) {
        result += '...';
        currentLength++;

        // Close all open tags
        for (let j = openTags.length - 1; j >= 0; j--) {
          result += `</${openTags[j]}>`;
        }
        break;
      }
    }
  }

  return result;
}

export function ChangeLogContent() {
  const { lang } = useTranslation('common');
  const [changelog, setChangelog] = useState<ChangeLog | null>(null);

  useEffect(() => {
    const fetchChangelogData = async () => {
      const fetchedChangelog = await fetchChangelog({ language: lang === 'pl' ? 'pl' : 'en' });
      setChangelog(fetchedChangelog);
    };
    fetchChangelogData();
  }, [lang]);

  if (!changelog || changelog?.items?.length === 0) {
    return null;
  }

  const lastItem = changelog.items[changelog.items.length - 1];
  const convertedLastItem = {
    id: 0,
    name: lastItem.header,
    link: ROUTES.CHANGELOG,
    description: truncateHtmlContent(lastItem.content, 300),
    icon: '',
    injectHtml: true,
  };

  return (
    <div>
      <div className={styles.changeLogWrapper}>
        <div>
          <Typography.Header transform="uppercase" color="#FC7E00" size="xs" tagType="div" gutterBottom={false}>
            Changelog <NextImage src="/img/icons/link.svg" alt="link" width={12} height={12} />
          </Typography.Header>
          <Divider color="#FC7E00" />
          <LinkInExpandedMenu item={convertedLastItem} />
        </div>
        <NextImage
          className={styles.image}
          src={changelog.items[changelog.items.length - 1]?.imageUrl}
          alt={changelog.items[changelog.items.length - 1]?.header}
          width={140}
          height={140}
        />
      </div>
    </div>
  );
}
